import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ShieldCheck, ShieldX, Loader2, AlertTriangle } from 'lucide-react';

/**
 * Consent screen for the remote MCP server (Claude connector).
 * Reached via redirect from the isolated MCP process's /authorize endpoint.
 * Only the single system owner (MCP_ALLOWED_USER_ID) can approve.
 */
function McpConsent({ user }) {
  const [searchParams] = useSearchParams();
  const ticket = searchParams.get('ticket');
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  useEffect(() => {
    if (!ticket) {
      setError('Link de autorização inválido: parâmetro "ticket" ausente.');
      setLoading(false);
      return;
    }
    api.get(`/mcp-consent/${ticket}`)
      .then((r) => setInfo(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'Não foi possível carregar esta solicitação.'))
      .finally(() => setLoading(false));
  }, [ticket]);

  const decide = async (approve) => {
    setActing(true);
    try {
      const r = await api.post(`/mcp-consent/${ticket}/${approve ? 'approve' : 'deny'}`);
      window.location.href = r.data.redirect_url;
    } catch (e) {
      setError(e?.response?.data?.detail || 'Não foi possível concluir a operação.');
      setActing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-6" data-testid="mcp-consent-card">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="w-6 h-6 text-emerald-700" />
          <h1 className="text-lg font-semibold text-gray-900">Autorizar conector Claude</h1>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-gray-500 py-6" data-testid="mcp-consent-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Carregando solicitação...
          </div>
        )}

        {!loading && error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm flex gap-2" data-testid="mcp-consent-error">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && info && (
          <>
            <p className="text-sm text-gray-600 mb-4">
              <strong className="text-gray-900">{info.client_name}</strong> está pedindo acesso
              SOMENTE LEITURA aos dados de projetos do sistema, agindo em seu nome como{' '}
              <strong>{user?.full_name || user?.email}</strong>.
            </p>
            <p className="text-xs text-gray-400 mb-5">
              Nenhum dado será criado, alterado ou excluído. Nomes de clientes e responsáveis
              são enviados de forma anonimizada.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => decide(true)}
                disabled={acting}
                className="flex-1 bg-emerald-700 hover:bg-emerald-800"
                data-testid="mcp-consent-approve-button"
              >
                {acting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                Autorizar
              </Button>
              <Button
                onClick={() => decide(false)}
                disabled={acting}
                variant="outline"
                className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
                data-testid="mcp-consent-deny-button"
              >
                <ShieldX className="w-4 h-4 mr-2" />
                Negar
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

export default McpConsent;
